import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid2 as Grid, Typography, Box, Tooltip, Fab } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useWatch } from 'react-hook-form';
import FormInput from 'components/Form/Input/Input';
import FormSelect from 'components/Form/Select/Select';
import Button from 'components/Buttons/Button';

type AnyField = { id: string } & Record<string, any>;

const IDENTIFIER_TYPE_INSURANCE = 'insurance';
const IDENTIFIER_TYPE_BIRTH_NUMBER = 'birthNumber';
const IDENTIFIER_TYPE_OTHER = 'other';
const IDENTIFIER_TYPE_LICENSE_NUMBER = 'licenseNumber';
const IDENTIFIER_TYPE_ICO = 'ico';

const INSURANCE_SYSTEM = 'https://ncez.mzcr.cz/fhir/sid/cpoj';
const LICENSE_NUMBER_SYSTEM = 'http://hl7.cz/fhir/NamingSystem/cz-doctor-license';

type Props = {
  control: any;
  compactMode?: boolean;

  identifierFields: AnyField[];
  appendIdentifier: (v: any) => void;
  removeIdentifier: (index: number) => void;

  telecomFields: AnyField[];
  appendTelecom: (v: any) => void;
  removeTelecom: (index: number) => void;

  addressFields: AnyField[];
  appendAddress: (v: any) => void;
  removeAddress: (index: number) => void;

  allowedContactSystems?: string[];
  fixedContactUse?: string;
  fixedAddressUse?: string;
  fixedAddressType?: string;
  identifierInputMode?: 'default' | 'patient' | 'practitioner';
};

const SharedContactSections: React.FC<Props> = ({
  control,
  compactMode = false,
  identifierFields,
  appendIdentifier,
  removeIdentifier,
  telecomFields,
  appendTelecom,
  removeTelecom,
  addressFields,
  appendAddress,
  removeAddress,
  allowedContactSystems,
  fixedContactUse,
  fixedAddressUse,
  fixedAddressType,
  identifierInputMode = 'default',
}) => {
  const { t } = useTranslation('PatientRegistry');
  const isAdvancedIdentifierMode = identifierInputMode !== 'default';
  const identifierValues = useWatch({ control, name: 'identifier' }) || [];

  const identifierSystemOptions =
    identifierInputMode === 'practitioner'
      ? [
          { id: IDENTIFIER_TYPE_LICENSE_NUMBER, label: t('identifierTypeLicenseNumber') },
          { id: IDENTIFIER_TYPE_ICO, label: t('identifierTypeIco') },
          { id: IDENTIFIER_TYPE_OTHER, label: t('other') },
        ]
      : [
          { id: IDENTIFIER_TYPE_BIRTH_NUMBER, label: t('identifierTypeBirthNumber') },
          { id: IDENTIFIER_TYPE_INSURANCE, label: t('identifierTypeInsurance') },
          { id: IDENTIFIER_TYPE_OTHER, label: t('other') },
        ];

  const getDefaultIdentifier = () => {
    if (identifierInputMode === 'practitioner') {
      return {
        system: LICENSE_NUMBER_SYSTEM,
        value: '',
        use: 'official',
        uiSystemType: IDENTIFIER_TYPE_LICENSE_NUMBER,
        oid: '',
      };
    }
    if (identifierInputMode === 'patient') {
      return {
        system: INSURANCE_SYSTEM,
        value: '',
        use: 'official',
        uiSystemType: IDENTIFIER_TYPE_INSURANCE,
        oid: '',
      };
    }
    return { system: '', value: '', use: 'official' };
  };

  const identifierUseOptions = [
    { id: 'official', label: t('useOfficial') },
    { id: 'usual', label: t('useUsual') },
    { id: 'temp', label: t('useTemp') },
    { id: 'secondary', label: t('useSecondary') },
  ];

  const contactSystemOptions = [
    { id: 'phone', label: t('phone') },
    { id: 'fax', label: t('fax') },
    { id: 'email', label: t('email') },
    { id: 'url', label: t('url') },
  ].filter((o) => !allowedContactSystems || allowedContactSystems.includes(o.id));

  const contactUseOptions = [
    { id: 'home', label: t('contactHome') },
    { id: 'work', label: t('contactWork') },
    { id: 'temp', label: t('contactTemp') },
    { id: 'mobile', label: t('contactMobile') },
  ];

  const addressUseOptions = [
    { id: 'home', label: t('addressHome') },
    { id: 'work', label: t('addressWork') },
    { id: 'temp', label: t('addressTemp') },
    { id: 'billing', label: t('addressBilling') },
  ];

  const addressTypeOptions = [
    { id: 'postal', label: t('addressPostal') },
    { id: 'physical', label: t('addressPhysical') },
    { id: 'both', label: t('addressBoth') },
  ];

  return (
    <>
      {/* Identifiers Section */}
      <Grid size={{ xs: 12 }}>
        <Box display="flex" alignItems="center">
          <Tooltip title={t('addIdentifier')}>
            <Box sx={{ mb: 1, mr: 2 }}>
              <Fab
                sx={{ mt: 1 }}
                aria-label="add"
                size="small"
                color="primary"
                onClick={() => appendIdentifier(getDefaultIdentifier())}
              >
                <AddIcon sx={{ fontSize: 18, color: '#fff' }} />
              </Fab>
            </Box>
          </Tooltip>
          <Typography variant="h6">{t('identifiers')}</Typography>
        </Box>
      </Grid>

      {identifierFields.map((field, index) => (
        <Grid size={{ xs: 12 }} key={field.id}>
          <Grid container spacing={compactMode ? 1 : 2}>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              {isAdvancedIdentifierMode ? (
                <FormSelect
                  control={control}
                  name={`identifier.${index}.uiSystemType`}
                  label={t('identifierType')}
                  items={identifierSystemOptions}
                />
              ) : (
                <FormInput
                  control={control}
                  name={`identifier.${index}.system`}
                  label={t('identifierSystem')}
                />
              )}
            </Grid>
            {isAdvancedIdentifierMode &&
              identifierValues[index]?.uiSystemType === IDENTIFIER_TYPE_OTHER && (
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <FormInput
                    control={control}
                    name={`identifier.${index}.oid`}
                    label="OID"
                    required={true}
                  />
                </Grid>
              )}
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <FormInput
                control={control}
                name={`identifier.${index}.value`}
                label={t('identifierValue')}
                required={isAdvancedIdentifierMode}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <FormSelect
                control={control}
                name={`identifier.${index}.use`}
                label={t('identifierUse')}
                items={identifierUseOptions}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 1 }}>
              <Box display="flex" justifyContent="flex-end" sx={{ pt: 1 }}>
                <Button
                  startIcon={<DeleteIcon />}
                  onClick={() => removeIdentifier(index)}
                  color="error"
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      ))}

      {/* Telecom Section */}
      <Grid size={{ xs: 12 }}>
        <Box display="flex" alignItems="center">
          <Tooltip title={t('addContact')}>
            <Box sx={{ mb: 1, mr: 2 }}>
              <Fab
                sx={{ mt: 1 }}
                aria-label="add"
                size="small"
                color="primary"
                onClick={() =>
                  appendTelecom({
                    system: allowedContactSystems?.[0] || 'phone',
                    value: '',
                    use: fixedContactUse || 'work',
                  })
                }
              >
                <AddIcon sx={{ fontSize: 18, color: '#fff' }} />
              </Fab>
            </Box>
          </Tooltip>
          <Typography variant="h6">{t('contacts')}</Typography>
        </Box>
      </Grid>

      {telecomFields.map((field, index) => (
        <Grid size={{ xs: 12 }} key={field.id}>
          <Grid container spacing={compactMode ? 1 : 2}>
            <Grid size={{ xs: 12, md: fixedContactUse ? 5 : 6, lg: fixedContactUse ? 5 : 4 }}>
              <FormSelect
                control={control}
                name={`telecom.${index}.system`}
                label={t('contactSystem')}
                items={contactSystemOptions}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: fixedContactUse ? 6 : 4 }}>
              <FormInput
                control={control}
                name={`telecom.${index}.value`}
                label={t('contactValue')}
              />
            </Grid>
            {!fixedContactUse && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <FormSelect
                  control={control}
                  name={`telecom.${index}.use`}
                  label={t('contactUse')}
                  items={contactUseOptions}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 1, lg: 1 }}>
              <Box display="flex" justifyContent="flex-end" sx={{ pt: 1 }}>
                <Button
                  startIcon={<DeleteIcon />}
                  onClick={() => removeTelecom(index)}
                  color="error"
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      ))}

      {/* Address Section */}
      <Grid size={{ xs: 12 }}>
        <Box display="flex" alignItems="center">
          <Tooltip title={t('addAddress')}>
            <Box sx={{ mb: 1, mr: 2 }}>
              <Fab
                sx={{ mt: 1 }}
                aria-label="add"
                size="small"
                color="primary"
                onClick={() =>
                  appendAddress({
                    use: fixedAddressUse || 'work',
                    type: fixedAddressType || 'physical',
                    line: [''],
                    city: '',
                    state: '',
                    postalCode: '',
                    country: '',
                  })
                }
              >
                <AddIcon sx={{ fontSize: 18, color: '#fff' }} />
              </Fab>
            </Box>
          </Tooltip>
          <Typography variant="h6">{t('addresses')}</Typography>
        </Box>
      </Grid>

      {addressFields.map((field, index) => (
        <Grid size={{ xs: 12 }} key={field.id}>
          <Box
            sx={{
              p: compactMode ? 1 : 2,
              border: '1px solid #ddd',
              borderRadius: 1,
              mb: compactMode ? 1 : 2,
            }}
          >
            <Grid container spacing={compactMode ? 1 : 2}>
              {!fixedAddressUse && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormSelect
                    control={control}
                    name={`address.${index}.use`}
                    label={t('addressUse')}
                    items={addressUseOptions}
                  />
                </Grid>
              )}
              {!fixedAddressType && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormSelect
                    control={control}
                    name={`address.${index}.type`}
                    label={t('addressType')}
                    items={addressTypeOptions}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                <FormInput
                  control={control}
                  name={`address.${index}.line[0]`}
                  label={t('address')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                <FormInput control={control} name={`address.${index}.city`} label={t('city')} />
              </Grid>
              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                <FormInput control={control} name={`address.${index}.state`} label={t('state')} />
              </Grid>
              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                <FormInput
                  control={control}
                  name={`address.${index}.postalCode`}
                  label={t('postalCode')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                <FormInput
                  control={control}
                  name={`address.${index}.country`}
                  label={t('country')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                <Box display="flex" justifyContent="flex-end" sx={{ pt: 1 }}>
                  <Button
                    startIcon={<DeleteIcon />}
                    onClick={() => removeAddress(index)}
                    color="error"
                    size="small"
                  >
                    {t('removeAddress')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      ))}
    </>
  );
};

export default SharedContactSections;
